import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { AppRole } from "@somasi/shared";
import { ROLE_LABELS as SHARED_ROLE_LABELS, ADMIN_ROLES, EDIT_ROLES } from "@somasi/shared";
import {
  getSession,
  onAuthStateChange,
  signOut as cognitoSignOut,
  type AuthSession,
  type AuthUser,
} from "@/integrations/aws/auth";
import { PAYROLL_READ_ROLES, PAYROLL_WRITE_ROLES } from "@somasi/shared";
import { bootstrapAccess, fetchMe } from "@/integrations/aws/api";

export type { AppRole };
export const ROLE_LABELS: Record<AppRole, string> = SHARED_ROLE_LABELS;

interface AuthState {
  session: AuthSession | null;
  user: AuthUser | null;
  roles: AppRole[];
  loading: boolean;
  hasRole: (r: AppRole) => boolean;
  hasAny: (rs: AppRole[]) => boolean;
  canEdit: boolean;
  canAdmin: boolean;
  /** May see what people are paid — narrower than `canAdmin`, and not a subset of it. */
  canPayroll: boolean;
  /**
   * May change a contract, a rate, or the register itself.
   *
   * Narrower again than `canPayroll`: `outsourced_admin` may read what somebody
   * is paid without being able to change it, and the People register is now
   * behind this one rather than behind a role of its own.
   */
  canPayrollWrite: boolean;
  refreshRoles: () => Promise<void>;
  signOut: () => Promise<void>;
}

/** Pauses before each further `/me` attempt on the way into a session. */
const ROLE_RETRY_DELAYS = [500, 1500];

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  /** Bumped on every load, so a slow answer for an earlier session cannot land on a later one. */
  const latest = useRef(0);

  /**
   * Roles live in DynamoDB rather than the token, so `/me` is the authority on
   * what the signed-in user may do. The bootstrap call is the idempotent repair
   * path for the designated owner and is safe to attempt on every sign-in.
   *
   * `/me` is retried rather than trusted on the first answer. A first sign-in
   * meets cold functions, and it reads roles through an index that can trail a
   * write made seconds earlier by the confirmation trigger or the bootstrap — so
   * a failure, or an empty answer, is asked again before it is believed.
   */
  const loadRoles = async (userId: string | null) => {
    const run = ++latest.current;
    if (!userId) {
      setRoles([]);
      setLoading(false);
      return;
    }

    let granted: AppRole[] = [];
    try {
      granted = (await bootstrapAccess()).roles ?? [];
    } catch (error) {
      console.warn("Unable to verify bootstrap access", error);
    }

    let found: AppRole[] = [];
    for (let attempt = 0; ; attempt++) {
      try {
        found = (await fetchMe())?.roles ?? [];
        if (found.length > 0 || granted.length > 0) break;
      } catch (error) {
        if (attempt >= ROLE_RETRY_DELAYS.length) console.error("Could not load roles", error);
      }
      if (attempt >= ROLE_RETRY_DELAYS.length || run !== latest.current) break;
      await new Promise((resolve) => setTimeout(resolve, ROLE_RETRY_DELAYS[attempt]));
    }

    if (run !== latest.current) return;
    setRoles([...new Set([...found, ...granted])]);
    setLoading(false);
  };

  useEffect(() => {
    // `loading` covers the roles as well as the session, so a fresh sign-in
    // holds the app until the answer is in rather than drawing it role-less.
    const subscription = onAuthStateChange((_event, next) => {
      setSession(next);
      if (next?.user) setLoading(true);
      void loadRoles(next?.user?.id ?? null);
    });

    void getSession().then((restored) => {
      // A sign-in that landed meanwhile has already started its own load.
      if (latest.current > 0) return;
      setSession(restored);
      return loadRoles(restored?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const hasRole = (r: AppRole) => roles.includes(r);
  const hasAny = (rs: AppRole[]) => rs.some((r) => roles.includes(r));

  const value: AuthState = {
    session,
    user: session?.user ?? null,
    roles,
    loading,
    hasRole,
    hasAny,
    canEdit: hasAny(EDIT_ROLES),
    canAdmin: hasAny(ADMIN_ROLES),
    canPayroll: hasAny(PAYROLL_READ_ROLES),
    canPayrollWrite: hasAny(PAYROLL_WRITE_ROLES),
    refreshRoles: () => loadRoles(session?.user?.id ?? null),
    signOut: async () => {
      await cognitoSignOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const c = useContext(AuthContext);
  if (!c) throw new Error("useAuth must be used inside AuthProvider");
  return c;
}
